export interface PzRacingDataRow {
  time: number
  longitude: number
  latitude: number
  direction: number
  speed: number
  date: Date
}

export interface I2mDataRow {
  time: number
  longitude: number
  latitude: number
  direction: number
  speed: number
  date: Date
  altitude: number
}

export interface AimDataRow {
  time: number
  longitude: number
  latitude: number
  direction: number
  speed: number
  date: Date
  altitude: number
}

export interface CsvDataRow {
  cts: number
  longitude: number
  latitude: number
  heading: number
  speed?: number
  date: Date
  altitude?: number
  gforceX?: number
  gforceY?: number
  gforceZ?: number
  lap?: number
  leanAngle?: number
  gyroX?: number
  gyroY?: number
  gyroZ?: number
}

export interface Coordinate {
  longitude: number
  latitude: number
}

export interface UpsertLapRequest {
  lap: LapDto
}

export interface LapDto {
  id: string
  startTime: Date
  finishTime: Date
  lapTimeMs: number
  telemetry?: TelemetryDto
  trackSessionId: string
  weather: Weather
  trackId: string
  lapNumber: number
  driverName?: string
}

export enum WeatherCondition {
  Unknown,
  Dry,
  Wet,
  Damp,
}

export interface Weather {
  condition: WeatherCondition
  temperatureInCelsius?: number
}

export interface CreateTrackSessionRequest {
  name: string
  trackId: string
  laps: LapDto[]
  telemetry: TelemetryDto
  weather: Weather
}

export interface DriverDto {
  id: Guid
  name: string
}

export interface CreateVehicleRequest {
  name: string
  type: string
  brand: string
  isPrimary: boolean
  manufactureYear?: number
  model: string
  weightKg?: number
  engine: VehicleEngineDto
}
export interface VehicleDto {
  id: Guid
  name: string
  type: string
  brand: string
  manufactureYear?: number
  model: string
  weightKg?: number
  engine: VehicleEngineDto
  isPrimary: boolean
}

export interface VehicleEngineDto {
  capacityCc?: number
  powerKw?: number
  torqueNm?: number
}

export enum TelemetryType {
  Gps = 'Gps',
  Gyroscope = 'Gyroscope',
  Accelerometer = 'Accelerometer',
  Device = 'Device',
}

export interface TrackSessionDto {
  id: Guid
  name: string
  trackId: Guid
  laps: Record<number, Guid>
  telemetry: TelemetryDto
  metadata: TrackSessionMetadataDto
}

export interface TrackSessionMetadataDto {
  fastestLapTime?: string
  fastLapNumber?: number
  totalSessionTime?: string
  totalSessionDistance?: number
  sessionStartTime?: Date
  weather?: Weather
}

export interface LatLngDto {
  latitude: number
  longitude: number
  altitude?: number | undefined
  speed: Speed
  time: DeviceTime
  distanceTravelled?: number | undefined
}

export interface Speed {
  kph: number
}

export interface Device {
  name: string
  version?: string
}

export interface TelemetryDto {
  device?: Device
  gps?: Gps
  gyroscope?: Gyroscope
  accelerometer?: Accelerometer
  diagnostics?: TelemetryDiagnosticsDto
}

export interface TelemetryDiagnosticsDto {
  /**
   * IMU-to-GPS clock offset measured over the whole recording session, ms,
   * positive = IMU behind GPS. A lap processed on its own applies it instead
   * of re-measuring over its short span.
   */
  imuLagMs?: number
  /**
   * Constant lean-rate offset of the calibrated gyro measured over the
   * whole recording session, deg/s (left positive). A lap processed on its
   * own removes it instead of re-measuring over its short span.
   */
  gyroLeanRateBiasDegS?: number
}

export interface Gps {
  positions: LatLngDto[]
}

export interface Gyroscope {
  unit: string
  direction: Direction
  gyroReading: GyroReading[]
}

export interface Accelerometer {
  unit: string
  direction: Direction
  accelerationReading: AccelerationReading[]
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface Direction {}

export interface DeviceTime {
  synchronizationTime?: number
  recordedTime: Date
}

export interface GyroReading {
  time: DeviceTime
  x: number
  y: number
  z: number
}

export interface AccelerationReading {
  time: DeviceTime
  x: number
  y: number
  z: number
}

export interface TrackDto {
  id: string
  name: string
  finishLine: Coordinate[]
  countryCode: string
  isUserSuggested: boolean
  venueName?: string
  lengthMeters?: number
}

/** Result from the nearby-tracks endpoint (predefined tracks within 2km). */
export interface NearbyTrackResult {
  id: string
  name: string
  layout: string
  lengthMeters: number
}

/** Result from changing the track assigned to a session. */
export interface TrackChangeResult {
  trackId: string
  trackName: string
  lapCount: number
}

export interface UserSuggestedTrack {
  track: TrackDto
  userId?: Guid
  status: UserSuggestedTrackStatus
}

export enum UserSuggestedTrackStatus {
  Unknown = 0,
  Pending,
  Rejected,
  Approved,
}

export type Guid = string & { isGuid: true }

export function guid(guid: string): Guid {
  return guid as Guid // maybe add validation that the parameter is an actual guid ?
}
